import React, { Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';

import { DetailsCard } from '@amzn/imdb-shared-meridian-components/components/DetailsCard';
import { GenericErrorCard } from '@amzn/imdb-shared-meridian-components/components/error/GenericErrorCard';
import filterTokens from '@amzn/meridian-tokens/base/icon/filter';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import DateRangePicker from '@amzn/meridian/date-range-picker';
import Divider from '@amzn/meridian/divider';
import Icon from '@amzn/meridian/icon';
import Input from '@amzn/meridian/input';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { daysFromToday, formatIso } from '../../../utils/date';
import { ListsTextItemsDataTable, SelectedTextItems } from './components/ListsTextItemsDataTable';
import { ListsUserActivityDataTable, SelectedCustomers } from './components/ListsUserActivityDataTable';
import { ListsTextSearchFormInput, ListsTextSearchFormResponseData } from './ListsTextSearchForm';

interface ListsTextSearchFormViewProps {
    inputData: ListsTextSearchFormInput;
    onInputChange: (_: Partial<ListsTextSearchFormInput>) => void;
    selectedTextItems: SelectedTextItems;
    setSelectedTextItems: Dispatch<SetStateAction<SelectedTextItems>>;
    selectedCustomers: SelectedCustomers;
    setSelectedCustomers: Dispatch<SetStateAction<SelectedCustomers>>;
    responseData: ListsTextSearchFormResponseData;
    onSubmit: () => void;
    canLoadMore: boolean;
    onLoadMore: () => void;
    fetchingCustomerIds: boolean;
    onAnalyzeSelection: () => void;
}

export const TEST_ID = {
    SEARCH_TERM_INPUT: 'search_term_input',
    FIND_LISTS_BUTTON: 'find_lists_button',
    LOAD_MORE_BUTTON: 'load_more_button',
    TEXT_ITEM_COLUMN: 'text_item_column',
    USER_ACTIVITY_COLUMN: 'user_activity_column',
    LIST_SEARCH_ERROR_CARD: 'list_search_error_card'
};

export const EMPTY_DATA_TEXT = 'No lists and users found';

export const ListsTextSearchFormView: React.VFC<ListsTextSearchFormViewProps> = (props) => {
    const {
        inputData,
        onInputChange,
        selectedTextItems,
        selectedCustomers,
        onSubmit,
        canLoadMore,
        onLoadMore,
        fetchingCustomerIds,
        onAnalyzeSelection
    } = props;

    const setDates = (dates: string[]) => {
        onInputChange({ startDate: dates[0], endDate: dates[1] });
    };

    const renderSearch = () => {
        const today = formatIso(new Date());
        const threeDaysAgo = formatIso(daysFromToday(-2));
        const sevenDaysAgo = formatIso(daysFromToday(-6));

        return (
            <VerticalSpacer>
                <Column>
                    <Row>
                        <Input
                            data-testid={TEST_ID.SEARCH_TERM_INPUT}
                            value={inputData.searchTerm}
                            onChange={(value) => onInputChange({ searchTerm: value })}
                            placeholder={'Enter spam text to search'}
                            width={'400px'}
                        />
                    </Row>
                    <Row>
                        <Text type='h100'>Look at regular lists modified between</Text>
                    </Row>
                    <Row width='100%' widths={['fit', 'fit', '200']} spacing='400' alignmentVertical='bottom'>
                        <DateRangePicker
                            value={[inputData.startDate, inputData.endDate]}
                            onChange={setDates}
                            startLabel='Start date'
                            endLabel='End date'
                            monthsInView={2}
                            presets={[
                                { label: 'Today', value: [today, today] },
                                { label: 'Last 3 days', value: [threeDaysAgo, today] },
                                { label: 'Last 7 days', value: [sevenDaysAgo, today] }
                            ]}
                            allowSameStartAndEnd={true}
                            clearButton={true}
                        />
                        <Button onClick={onSubmit} data-testid={TEST_ID.FIND_LISTS_BUTTON}>
                            Find lists
                        </Button>
                        {Object.keys(selectedTextItems).filter((k) => selectedTextItems[k]).length ||
                        Object.keys(selectedCustomers).filter((k) => selectedCustomers[k]).length ? (
                            <HorizontalSpacer style={{ textAlign: 'right' }}>
                                {fetchingCustomerIds ? (
                                    <HorizontalSpacer
                                        role='selection-loader'
                                        style={{ width: '200px', display: 'inline-block' }}
                                    >
                                        <Loader size='small' type='linear'></Loader>
                                    </HorizontalSpacer>
                                ) : (
                                    <Button type='tertiary' onClick={onAnalyzeSelection}>
                                        <Icon tokens={filterTokens} /> Analyze selection
                                    </Button>
                                )}
                            </HorizontalSpacer>
                        ) : null}
                    </Row>
                    {canLoadMore ? (
                        <Alert>
                            <Row spacing='300' width='330px'>
                                <Text type='h100'>There are more items to load.</Text>
                                <Button
                                    size='small'
                                    type='tertiary'
                                    data-testid={TEST_ID.LOAD_MORE_BUTTON}
                                    onClick={onLoadMore}
                                >
                                    Load More
                                </Button>
                            </Row>
                        </Alert>
                    ) : null}
                </Column>
            </VerticalSpacer>
        );
    };

    return (
        <Container>
            {renderSearch()}
            <Divider />
            <VerticalSpacer>
                <MainColumn {...props} />
            </VerticalSpacer>
        </Container>
    );
};

type MainColumnProps = Pick<
    ListsTextSearchFormViewProps,
    'responseData' | 'selectedTextItems' | 'setSelectedTextItems' | 'selectedCustomers' | 'setSelectedCustomers'
>;

const MainColumn = ({
    responseData,
    selectedCustomers,
    selectedTextItems,
    setSelectedCustomers,
    setSelectedTextItems
}: MainColumnProps) => {
    if (responseData.fetching) {
        return <CustomLoader />;
    } else if (responseData.error) {
        return (
            <div data-testid={TEST_ID.LIST_SEARCH_ERROR_CARD}>
                <GenericErrorCard message={responseData?.error?.message ?? 'Unexpected error'} />
            </div>
        );
    }

    if (!responseData.textItemsData.length && !responseData.userActivityData.length) {
        return <Row alignmentVertical='top'>{EMPTY_DATA_TEXT}</Row>;
    }

    return (
        <Row alignmentVertical='top'>
            {responseData.textItemsData.length > 0 && (
                <Column width='50%' data-testid={TEST_ID.TEXT_ITEM_COLUMN}>
                    <DetailsCard
                        key={'Text Items'}
                        name={'Text Items'}
                        CustomCardContentElement={
                            <Column>
                                <ListsTextItemsDataTable
                                    textItems={responseData.textItemsData}
                                    selectableOptions={{
                                        idFieldName: 'text',
                                        selected: selectedTextItems,
                                        setSelected: setSelectedTextItems
                                    }}
                                />
                            </Column>
                        }
                    />
                </Column>
            )}
            {responseData.userActivityData.length > 0 && (
                <Column width='50%' data-testid={TEST_ID.USER_ACTIVITY_COLUMN}>
                    <DetailsCard
                        key={'Users'}
                        name={'Users'}
                        CustomCardContentElement={
                            <Column>
                                <ListsUserActivityDataTable
                                    userActivity={responseData.userActivityData}
                                    selectableOptions={{
                                        idFieldName: 'customerId',
                                        selected: selectedCustomers,
                                        setSelected: setSelectedCustomers
                                    }}
                                />
                            </Column>
                        }
                    />
                </Column>
            )}
        </Row>
    );
};

const CustomLoader = () => {
    return (
        <VerticalSpacer style={{ paddingTop: '100px' }}>
            <Row alignmentHorizontal='center' widths={['200px']}>
                <Column>
                    <Text alignment='center' type='h200'>
                        Fetching lists...
                    </Text>
                    <Loader size='small' type='linear' />
                </Column>
            </Row>
        </VerticalSpacer>
    );
};

const Container = styled.div`
    padding: 15px 10px;
`;

const VerticalSpacer = styled.div`
    padding: 15px 0;
`;

const HorizontalSpacer = styled.div`
    padding: 0 15px;
`;
